"use client";

import React, { useState } from "react";
import { CheckCheck, ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/startup-ui/button";
import { motion } from "framer-motion";
import { PlanDefinition } from "@/config/plansData";
import { toast } from "sonner";
import { createBillingPortalSession, createCheckoutSession, updateSubscriptionPlan } from "@/actions/stripeActions";
import { useAuth } from "@clerk/nextjs";
import { Doc } from "@/convex/_generated/dataModel";
import { api } from "@/convex/_generated/api";
import { useQuery } from "convex-helpers/react";
import ConfirmPlanChangeModal from "@/components/billing/confirm-plan-change-modal";
const APP_SIGNUP_URL = `${process.env.NEXT_PUBLIC_APP_URL || "http://app.localhost:3000"}/sign-up`;
const COOKIE_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || ".localhost";

// Define OrgSubscriptionStatus based on the query return type if possible
// Assuming it might look like this, adjust based on actual query response
type OrgSubscriptionStatus = NonNullable<ReturnType<typeof useQuery<typeof api.mainSaas.organizations.getOrgSubscriptionStatus>>["data"]>["data"];
interface PricingShortProps {
  plans: Doc<"plans">[];
  isLandinPage?: boolean;
  // Pass currentOrgStatus as a prop
  // currentOrgStatus?: OrgSubscriptionStatus | null;
}

// Fetch current organization's subscription status within the component
export function PricingShort({
  plans,
  isLandinPage
}: PricingShortProps) {
  const [active, setActive] = useState("monthly");
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const {
    isSignedIn,
    isLoaded,
    orgId
  } = useAuth();
  const [statusMessage, setStatusMessage] = useState("");
  // const [statusMessage, setStatusMessage] = useState("Loading...");

  // --- Modal State ---
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [planToChange, setPlanToChange] = useState<Doc<"plans"> | null>(null);
  // --- End Modal State ---

  // Fetch current organization's subscription status using useQuery
  const {
    data: orgSubscriptionData,
    isPending: isLoadingOrgStatus
  } = useQuery(api.mainSaas.organizations.getOrgSubscriptionStatusInternal, isLoaded ? {
    orgId: orgId ?? ""
  } : "skip");
  const currentOrgStatus = orgSubscriptionData?.data;
  const tabs = [{
    name: "Monthly",
    value: "monthly"
  }, {
    name: "Yearly",
    value: "yearly"
  }];
  const monthlyPlans = plans.filter(p => p.ltdCampaignIdentifier === "MONTHLY").sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const yearlyPlans = plans.filter(p => p.ltdCampaignIdentifier === "YEARLY").sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Find corresponding yearly plan for savings calculation
  const firstMonthly = monthlyPlans[0];
  const firstYearly = yearlyPlans.find(p => p.stackLevel === firstMonthly?.stackLevel); // Match based on stackLevel or other logic
  let savingsPercentage = 0;
  if (firstMonthly && firstYearly && firstMonthly.targetPrice && firstYearly.targetPrice) {
    const monthlyCost = firstMonthly.targetPrice;
    const effectiveMonthlyFromYearly = firstYearly.targetPrice / 12;
    savingsPercentage = Math.round((monthlyCost - effectiveMonthlyFromYearly) / monthlyCost * 100);
  }
  const displayedPlans = active === "monthly" ? monthlyPlans : yearlyPlans;

  // --- Function to open the confirmation modal ---
  const openConfirmationModal = (plan: Doc<"plans">) => {
    setPlanToChange(plan);
    setIsConfirmModalOpen(true);
  };

  // --- Function called when modal confirmation is clicked ---
  const handleConfirmPlanChange = async (planToSwitchTo: Doc<"plans">) => {
    if (!orgId || !isSignedIn) return; // Extra safety check

    setLoadingPlan(planToSwitchTo.lookupKey); // Set loading state for the modal button
    setStatusMessage(`Updating subscription...`); // Optional: message inside modal or general

    try {
      const isYearlySelected = active === "yearly";
      const updateResult = await updateSubscriptionPlan(planToSwitchTo.lookupKey, isYearlySelected);
      if (updateResult.error) {
        throw new Error(updateResult.error.message);
      } else {
        toast.success(`Subscription update initiated! Your plan will change shortly.`);
        // setStatusMessage("Update successful! Changes may take a moment.");
        setIsConfirmModalOpen(false); // Close modal on success
        setPlanToChange(null);
        // Optional: Refresh data after delay or use query invalidation
        // setTimeout(() => window.location.reload(), 3000);
      }
    } catch (error: any) {
      toast.error(`Update failed: ${error.message}`);
      setStatusMessage(""); // Clear message on error
    } finally {
      setLoadingPlan(null); // Clear loading state regardless of outcome for the modal
    }
  };

  // Handle Plan Action Click (Revised Logic for Modal)
  const handlePlanAction = async (plan: Doc<"plans">) => {
    if (!isLoaded) return;

    // Handle Not Signed In
    if (!isSignedIn) {
      console.log(`User not signed in. Setting cookie for plan: ${plan.lookupKey}`);
      toast.info("Redirecting to sign up...");
      window.location.href = APP_SIGNUP_URL;
      return;
    }

    // Handle No Org Selected
    if (!orgId) {
      toast.error("Please select or create an organization first.");
      return;
    }
    setLoadingPlan(plan.lookupKey); // Set loading early for the clicked button
    setStatusMessage("");
    const hasActivePaidPlan = currentOrgStatus?.stripeSubscriptionStatus === "active" && currentOrgStatus?.planType === "monthly" || currentOrgStatus?.lifetimeAccess === true;
    const isCurrentPlan = currentOrgStatus?.planLookupKey === plan.lookupKey;
    // const isOnTrial = currentOrgStatus?.isOnTrial ?? false; // Not used in this logic branch

    try {
      // A. Clicked Current Active Paid Plan OR User has LTD -> Go to Portal
      if (isCurrentPlan && hasActivePaidPlan) {
        setStatusMessage("Redirecting to manage subscription...");
        const result = await createBillingPortalSession();
        if (result.error) throw new Error(result.error.message);
        if (result.data?.url) window.location.href = result.data.url;else throw new Error("Could not get billing portal URL.");
        // Keep card button loading until redirect
      }
      // B. Has Active Paid Plan/LTD but clicked DIFFERENT Plan -> Open Confirmation Modal
      else if (hasActivePaidPlan && !isCurrentPlan) {
        console.log(`Clicked different plan (${plan.lookupKey}) while active. Opening confirmation modal.`);
        setLoadingPlan(null); // Stop card button loading
        openConfirmationModal(plan); // Open the modal
      }
      // C. No Active Paid Plan/LTD (Trial, Free, Canceled) -> Initiate Checkout
      else {
        console.log(`No active paid plan. Initiating checkout for ${plan.lookupKey}.`);
        setStatusMessage("Processing checkout...");
        const priceId = plan.stripePriceId;
        if (!priceId) throw new Error(`Price ID missing for plan ${plan.lookupKey}`);
        const isYearlySelected = active === "yearly";
        const result = await createCheckoutSession(priceId, plan.lookupKey, isYearlySelected);
        if (result.error) throw new Error(result.error.message);
        if (result.data?.url) window.location.href = result.data.url;else throw new Error("Could not get checkout URL.");
        // Keep card button loading until redirect
      }
    } catch (error: any) {
      console.error("[handlePlanAction Error]", error);
      toast.error(error.message || "An unexpected error occurred.");
      setLoadingPlan(null); // Ensure loading is cleared on error
      setStatusMessage("");
    }
    // Note: setLoadingPlan(null) is handled within branches or finally block of confirm handler
  };
  return <div id="pricing" className="relative isolate w-full bg-white px-4 py-0 dark:bg-neutral-950 sm:py-10 lg:px-4" data-sentry-component="PricingShort" data-sentry-source-file="pricing-short.tsx">
      {/* Monthly/Yearly Toggle - Adjusted margin/padding */}
      <div className={cn(isLandinPage ? "flex flex-row items-center justify-center pl-40" : "mb-0 ml-auto flex w-fit flex-col items-end justify-center md:mr-4")}>
        <div className="relative w-fit">
          {savingsPercentage > 0 && <div className={cn("absolute -top-6 right-0 z-10 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        // isLandinPage ? "hidden" : "",
        )}>
              Save {savingsPercentage}%
            </div>}
          <div className="flex w-fit items-center justify-center rounded-md bg-neutral-100 p-1 dark:bg-neutral-800">
            {tabs.map(tab => <button key={tab.value} className={cn("relative w-24 rounded-md p-2 text-sm font-medium text-gray-500 opacity-70 transition-colors", active === tab.value ? "text-white dark:text-black" : "")} onClick={() => setActive(tab.value)}>
                {active === tab.value && <motion.span layoutId="moving-div" transition={{
              type: "spring",
              bounce: 0.2,
              duration: 0.6
            }} className="absolute inset-0 rounded-md bg-black dark:bg-white" />}
                <span className="relative z-10">{tab.name}</span>
              </button>)}
          </div>
        </div>
      </div>

      {/* Status Message Area */}
      {statusMessage ? <div className="my-0 flex items-center justify-center gap-2 text-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {statusMessage}
        </div> : <div className="my-0 flex min-h-6 items-center justify-center gap-2 text-center text-muted-foreground">
          {/* <CheckCheck className="h-4 w-4" /> {statusMessage} */}
        </div>}

      {/* Pricing Cards */}
      <div className={cn("mx-auto mt-5 grid grid-cols-1 gap-4 ", "mx-auto max-w-7xl md:grid-cols-2 xl:grid-cols-3")}>
        {isLoadingOrgStatus && !currentOrgStatus ?
      // Show loader only if truly loading and no data yet
      <div className="col-span-full pt-10 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <p className="mt-2 text-muted-foreground">Loading plans...</p>
          </div> : displayedPlans.length > 0 ? displayedPlans.map(tier => {
        const isCurrent = currentOrgStatus?.planLookupKey === tier.lookupKey;
        const hasActivePaidPlan = currentOrgStatus?.stripeSubscriptionStatus === "active" && currentOrgStatus?.planType === "monthly" || currentOrgStatus?.lifetimeAccess === true;
        let buttonText = "Get Started";
        if (!isLoaded) {
          buttonText = "Loading...";
        } else if (isSignedIn && !orgId) {
          buttonText = "Select Org";
        } else if (isSignedIn && orgId) {
          if (hasActivePaidPlan) {
            buttonText = isCurrent ? "Manage Plan" : "Switch Plan";
          } else if (currentOrgStatus?.isOnTrial) {
            buttonText = "Upgrade";
          } else {
            buttonText = "Get Started";
          }
        } else {
          buttonText = "Get Started";
        }
        return <Card plan={tier} key={tier.lookupKey} onClick={() => handlePlanAction(tier)} billingPeriod={active} isLoading={loadingPlan === tier.lookupKey} dynamicButtonText={buttonText} isCurrentPlan={isCurrent && hasActivePaidPlan} />;
      }) : <p className="col-span-full pt-10 text-center text-muted-foreground">
            No {active} plans available at this moment.
          </p>}
      </div>

      {/* Confirmation Modal */}
      <ConfirmPlanChangeModal isOpen={isConfirmModalOpen} onClose={() => {
      setIsConfirmModalOpen(false);
      setPlanToChange(null);
    }}
    // Find current plan name more safely
    currentPlanName={currentOrgStatus?.planLookupKey ? plans.find(p => p.lookupKey === currentOrgStatus.planLookupKey)?.name : undefined} newPlan={planToChange} billingInterval={active as "monthly" | "yearly"} onConfirm={handleConfirmPlanChange} // Pass the confirmation handler
    isLoading={loadingPlan === planToChange?.lookupKey} // Modal button loading state
    data-sentry-element="ConfirmPlanChangeModal" data-sentry-source-file="pricing-short.tsx" />
    </div>;
}

// --- Card Subcomponent ---
const Card = ({
  plan,
  onClick,
  billingPeriod,
  isLoading,
  dynamicButtonText,
  // Receive dynamic button text
  isCurrentPlan
}: {
  plan: Doc<"plans">;
  onClick: () => void;
  billingPeriod: string;
  isLoading?: boolean;
  dynamicButtonText: string; // Use the passed text directly
  isCurrentPlan?: boolean;
}) => {
  const isYearly = billingPeriod === "yearly";

  // Always show monthly pricing, but calculate from yearly if applicable
  const actualBilledPrice = isYearly ? Math.round((plan.targetPrice || 0) / 12) : plan.initialPrice ?? plan.targetPrice;
  const billingPeriodText = "/month";

  // Use the dynamic text passed from parent, fallback to plan type if needed
  const buttonText = dynamicButtonText || (plan.planType === "ltd" ? "Buy Lifetime Deal" : "Get Started");
  const cardBaseClass = "rounded-3xl border p-1 sm:p-4 md:p-4";
  const cardInnerBaseClass = "flex h-full flex-col justify-start gap-4";
  const cardHeaderBaseClass = "w-full rounded-2xl p-4";
  const buttonBaseClass = "mt-10 w-full";

  // Simplified icon logic: Show ExternalLink only if it's the current plan
  const showPortalIcon = isCurrentPlan;
  if (plan.isFeatured) {
    return <div className={cn(cardBaseClass, "border-neutral-800 bg-[radial-gradient(164.75%_100%_at_50%_0%,#334155_0%,#0F172A_48.73%)]")}>
        <div className={cn(cardInnerBaseClass)}>
          <div className={cn(cardHeaderBaseClass, "bg-netural-800/50 shadow-[0px_-1px_0px_0px_var(--neutral-700)] shadow-input dark:bg-blue-900/20")}>
            {/* Featured Header Content */}
            <div className="flex items-start justify-between">
              <p className="text-lg font-medium text-white">
                {plan.name.replace(" Monthly", "").replace(" Yearly", "")}
              </p>
              {plan.isFeatured &&
            // Show featured badge only if isFeatured is true
            <div className="relative rounded-full bg-white px-3 py-1 text-xs font-medium text-black">
                  <div className="absolute inset-x-0 bottom-0 mx-auto h-px w-3/4 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
                  Featured
                </div>}
            </div>
            {/* Price Display */}
            <div className="mt-8">
              <div className="flex items-end">
                <span className="text-lg font-bold text-neutral-300">
                  {plan.currency === "usd" ? "$" : plan.currency}
                </span>
                <div className="flex items-start gap-2">
                  <motion.span key={`${billingPeriod}-${actualBilledPrice}`} // More specific key
                initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.3
                }} className="text-3xl font-bold text-white md:text-7xl">
                    {actualBilledPrice}
                  </motion.span>
                </div>
                <span className="mb-1 text-base font-normal text-neutral-300 md:mb-2">
                  {billingPeriodText}
                </span>
              </div>
              {billingPeriod === "monthly" && plan.initialPrice && plan.targetPrice && plan.initialPrice !== plan.targetPrice && <p className="-mb-6 mt-1 text-sm text-neutral-400 line-through ">
                    ${plan.targetPrice.toFixed(2)}
                    {billingPeriodText}
                  </p>}
              {isYearly && <p className="mt-1 text-sm text-neutral-300">
                  Billed annually (${plan.targetPrice}/year)
                </p>}
            </div>
            {/* Button */}
            <div className="mt-10 w-full">
              <Button variant="primary" className={cn(buttonBaseClass, "bg-white text-black hover:bg-white/90", isCurrentPlan ? "opacity-80" : "" // Style current plan button differently if needed
            )} onClick={onClick} disabled={isLoading || buttonText === "Select Organization"} // Disable if loading or no org
            >
                <span className="flex items-center justify-center">
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : showPortalIcon ?
                // Show icon for Current Plan only
                <ExternalLink className="mr-2 h-4 w-4" /> : null}
                  {isLoading ? "Processing..." : buttonText}
                </span>
              </Button>
            </div>
          </div>
          {/* Features */}
          <div className="mt-1 p-4">
            {plan.features.slice(0, 5).map((feature, idx) => <FeaturedStep key={idx}>{feature}</FeaturedStep>)}
          </div>
        </div>
      </div>;
  }

  // Non-featured card
  return <div className={cn(cardBaseClass, "border-gray-100 bg-gray-50 dark:border-neutral-800 dark:bg-neutral-900")} data-sentry-component="Card" data-sentry-source-file="pricing-short.tsx">
      <div className={cn(cardInnerBaseClass)}>
        <div className={cn(cardHeaderBaseClass, "bg-white shadow-input dark:bg-neutral-800 dark:shadow-[0px_-1px_0px_0px_var(--neutral-700)]")}>
          {/* Standard Header Content */}
          <div className="flex items-start justify-between">
            <p className="text-lg font-medium text-black dark:text-white">
              {plan.name.replace(" Monthly", "").replace(" Yearly", "")}
            </p>
          </div>
          {/* Price Display */}
          <div className="mt-8">
            <div className="flex items-end">
              <span className="text-lg font-bold text-neutral-500 dark:text-neutral-200">
                {plan.currency === "usd" ? "$" : plan.currency}
              </span>
              <div className="flex items-start gap-2">
                <motion.span key={`${billingPeriod}-${actualBilledPrice}`} // More specific key
              initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3
              }} className="text-3xl font-bold text-neutral-800 dark:text-neutral-50 md:text-7xl" data-sentry-element="unknown" data-sentry-source-file="pricing-short.tsx">
                  {actualBilledPrice}
                </motion.span>
              </div>
              <span className="mb-1 text-base font-normal text-neutral-500 dark:text-neutral-200 md:mb-2">
                {billingPeriodText}
              </span>
            </div>
            {billingPeriod === "monthly" && plan.initialPrice && plan.targetPrice && plan.initialPrice !== plan.targetPrice && <p className="-mb-6 mt-1 text-sm text-neutral-400 line-through">
                  ${plan.targetPrice.toFixed(2)}
                  {billingPeriodText}
                </p>}
            {isYearly && <p className="mt-1 text-sm text-neutral-400">
                Billed annually (${plan.targetPrice}/year)
              </p>}
          </div>
          {/* Button */}
          <div className="mt-10 w-full">
            <Button variant="gradient" className={cn(buttonBaseClass, isCurrentPlan ? "opacity-80" : "" // Style current plan button differently if needed
          )} onClick={onClick} disabled={isLoading || buttonText === "Select Organization"} // Disable if loading or no org
          data-sentry-element="Button" data-sentry-source-file="pricing-short.tsx">
              <span className="flex items-center justify-center">
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : showPortalIcon ?
              // Show icon for Current Plan only
              <ExternalLink className="mr-2 h-4 w-4" /> : null}
                {isLoading ? "Processing..." : buttonText}
              </span>
            </Button>
          </div>
        </div>
        {/* Features */}
        <div className="mt-1 p-4">
          {plan.features.slice(0, 5).map((feature, idx) => <Step key={idx}>{feature}</Step>)}
        </div>
      </div>
    </div>;
};
const Step = ({
  children,
  additional
}: {
  children: React.ReactNode;
  additional?: boolean;
}) => {
  return <div className="my-4 flex items-start justify-start gap-2" data-sentry-component="Step" data-sentry-source-file="pricing-short.tsx">
      <div className={cn("mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-neutral-700", additional ? "bg-sky-500" : "bg-neutral-700")}>
        <CheckCheck className="h-3 w-3 text-neutral-300 [stroke-width:4px]" data-sentry-element="CheckCheck" data-sentry-source-file="pricing-short.tsx" />
      </div>
      <div className="text-sm font-medium text-black dark:text-white">
        {children}
      </div>
    </div>;
};

// Featured version of Step component
const FeaturedStep = ({
  children,
  additional
}: {
  children: React.ReactNode;
  additional?: boolean;
}) => {
  return <div className="my-4 flex items-start justify-start gap-2" data-sentry-component="FeaturedStep" data-sentry-source-file="pricing-short.tsx">
      <div className={cn("mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full", additional ? "bg-sky-500" : "bg-white")}>
        <CheckCheck className="h-3 w-3 text-black [stroke-width:4px]" data-sentry-element="CheckCheck" data-sentry-source-file="pricing-short.tsx" />
      </div>
      <div className="text-sm font-medium text-neutral-200">{children}</div>
    </div>;
};